// "¿Dónde vas a utilizar este Tag?" — searchable advertising platform picker
// (7.6F.2). Server search (names, aliases, this workspace's own platforms),
// bounded and debounced; never a giant select. Keyboard: type, ↑/↓ to move,
// Enter to choose (cmdk combobox/listbox semantics). The selection is
// announced; status is text + icon, never color alone. Catalog presence is
// not compatibility: the summary is shown only after a platform is chosen.
import { Command } from "cmdk";
import { Building2, Plus, Search } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { CreativeProduct, PlatformSearchItem, PlatformType } from "@/api/types";
import { COMPATIBILITY_STATES, PLATFORM_TYPE_LABELS } from "@/lib/labels";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { cn } from "@/ui/cn";
import { AddPlatformDialog } from "./add-platform-dialog";
import { usePlatformSearch, type PlatformChoice } from "./queries";

export interface SelectedPlatform extends PlatformChoice {
  readonly name: string;
  readonly platformType: PlatformType;
}

function useDebounced<T>(value: T, ms: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      clearTimeout(t);
    };
  }, [value, ms]);
  return debounced;
}

/** Short status of a search hit: the capability-level status for the creative's product. */
function hitStatus(item: PlatformSearchItem): { label: string; tone: "success" | "warning" | "neutral" | "danger" } {
  if (item.kind === "custom" && !item.mappedTo) return { label: "Plataforma personalizada", tone: "neutral" };
  const s = item.capabilityStatus ?? "UNVERIFIED";
  if (s === "VERIFIED") return { label: "Verificada por InteractAds", tone: "success" };
  if (s === "LIMITED") return { label: "Con limitaciones", tone: "warning" };
  if (s === "UNSUPPORTED") return { label: "No compatible en nuestra prueba", tone: "danger" };
  return { label: COMPATIBILITY_STATES.UNVERIFIED.label, tone: "neutral" };
}

export function PlatformPicker({
  orgId,
  product,
  value,
  onChange,
  canAdd,
  error,
}: {
  orgId: string;
  product: CreativeProduct;
  value: SelectedPlatform | null;
  onChange: (value: SelectedPlatform | null) => void;
  canAdd: boolean;
  error?: string | undefined;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [announce, setAnnounce] = useState("");
  const q = useDebounced(query.trim(), 180);
  const search = usePlatformSearch(orgId, q, product, value === null);
  const items = search.data?.items ?? [];
  const searching = search.isFetching || q !== query.trim();
  const noResults = !searching && q !== "" && items.length === 0;

  const choose = (choice: SelectedPlatform) => {
    onChange(choice);
    setAnnounce(`Plataforma seleccionada: ${choice.name}.`);
  };

  return (
    <div className="grid gap-2">
      <p id={`${id}-question`} className="text-sm font-semibold">
        ¿Dónde vas a utilizar este Tag?
      </p>
      <p className="sr-only" aria-live="polite">
        {announce}
      </p>
      {value ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface p-3">
          <Building2 className="size-5 shrink-0 text-muted" aria-hidden="true" />
          <div className="grid min-w-0 flex-1">
            <span className="truncate text-sm font-medium" data-testid="selected-platform">
              {value.name}
            </span>
            <span className="text-xs text-muted">
              {PLATFORM_TYPE_LABELS[value.platformType]}
              {value.kind === "custom" ? " · Plataforma personalizada" : ""}
            </span>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              onChange(null);
              setAnnounce("Elige otra plataforma.");
              requestAnimationFrame(() => inputRef.current?.focus());
            }}
          >
            Cambiar
          </Button>
        </div>
      ) : (
        <Command
          shouldFilter={false}
          // cmdk names the combobox with this (hidden) label.
          label="Buscar DSP o plataforma"
          className="grid gap-1.5"
          loop
        >
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-subtle"
              aria-hidden="true"
            />
            <Command.Input
              ref={inputRef}
              id={`${id}-search`}
              value={query}
              onValueChange={setQuery}
              placeholder="Buscar DSP o plataforma"
              aria-describedby={`${id}-question${error ? ` ${id}-error` : ""}`}
              aria-invalid={error ? true : undefined}
              autoComplete="off"
              className={cn(
                "h-9 w-full rounded-md border border-line-strong bg-surface pl-8 pr-2.5 text-sm text-fg placeholder:text-subtle",
                error ? "border-danger" : null
              )}
            />
          </div>
          <Command.List
            className="max-h-64 overflow-y-auto rounded-md border border-line bg-surface p-1"
            aria-busy={searching || undefined}
          >
            {search.isError ? (
              <p className="px-2 py-3 text-sm text-danger">No pudimos buscar plataformas. Inténtalo de nuevo.</p>
            ) : null}
            {items.map((item) => {
              const status = hitStatus(item);
              return (
                <Command.Item
                  key={`${item.kind}-${item.id}`}
                  value={`${item.kind}-${item.id}`}
                  onSelect={() => {
                    choose({ kind: item.kind, id: item.id, name: item.name, platformType: item.platformType });
                  }}
                  className="flex cursor-default flex-wrap items-center gap-x-2 gap-y-1 rounded-sm px-2 py-1.5 text-sm aria-selected:bg-sunken"
                >
                  <Building2 className="size-4 shrink-0 text-muted" aria-hidden="true" />
                  <span className="font-medium">{item.name}</span>
                  <span className="text-xs text-muted">
                    {PLATFORM_TYPE_LABELS[item.platformType]}
                    {item.matchedAlias ? ` · también «${item.matchedAlias}»` : ""}
                  </span>
                  <Badge tone={status.tone} className="ml-auto normal-case tracking-normal">
                    {status.label}
                  </Badge>
                </Command.Item>
              );
            })}
            {noResults ? (
              <div className="grid justify-items-start gap-2 px-2 py-3 text-sm">
                <p className="font-medium">No encontramos esta plataforma</p>
                {canAdd ? (
                  <p className="text-muted">Puedes agregarla a tu espacio de trabajo y continuar.</p>
                ) : (
                  <p className="text-muted">Pide a un responsable de tu equipo que la agregue.</p>
                )}
              </div>
            ) : null}
            {canAdd && q !== "" && !searching ? (
              <Command.Item
                value="__add__"
                onSelect={() => {
                  setAdding(true);
                }}
                className="flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-brand aria-selected:bg-sunken"
              >
                <Plus className="size-4" aria-hidden="true" /> Agregar plataforma «{q}»
              </Command.Item>
            ) : null}
            {q === "" && items.length > 0 ? (
              <p className="px-2 pb-1 pt-2 text-2xs text-muted">
                Escribe para buscar por nombre o por un nombre anterior de la plataforma.
              </p>
            ) : null}
          </Command.List>
        </Command>
      )}
      {error ? (
        <p id={`${id}-error`} className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : null}
      <AddPlatformDialog
        key={adding ? `open:${query.trim()}` : "closed"}
        orgId={orgId}
        product={product}
        open={adding}
        initialName={query.trim()}
        onOpenChange={setAdding}
        onChosen={(choice) => {
          setAdding(false);
          choose(choice);
        }}
      />
    </div>
  );
}
